import { UseFormReturn } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { arcToFixed } from '@src/core/extensions';

import { IRouteForm, RouteWaypointEntity, RouteWaypointType } from '@src/features/order/pages/upsert-mission-page/entities';
import {
  selectEditableRoute,
  selectWaypoints,
  setEditableRoute,
  setRouteModified,
  setWaypoints,
} from '@src/features/order/pages/upsert-mission-page/redux';
import { roundWaypointCoords } from '@src/features/order/pages/upsert-mission-page/utils';

interface UseRouteEditToggleReturn {
  editableRoute: boolean;
  toggleEditableRoute: () => void;
}

// Shared "Modify Route" / "Apply Route" toggle so the route edit mode can be driven
// from either EnterRouteForm or the single-page Flight Plan footer with identical
// behavior: when leaving edit mode, coordinates are rounded and altitudes synced back
// into the form (which the mission payload reads).
export const useRouteEditToggle = (form: UseFormReturn<IRouteForm>): UseRouteEditToggleReturn => {
  const dispatch = useDispatch();
  const wps = useSelector(selectWaypoints);
  const editableRoute = useSelector(selectEditableRoute);

  const syncAltitudeFormValues = (waypoints: RouteWaypointEntity[]) => {
    const takeOff = waypoints.find((w) => w.type === RouteWaypointType.TAKE_OFF);
    const destination = waypoints.find((w) => w.type === RouteWaypointType.DESTINATION);
    if (takeOff) form.setValue('takeOffAltitude', arcToFixed(takeOff.altitude));
    if (destination) form.setValue('destinationAltitude', arcToFixed(destination.altitude));
    waypoints
      .filter((w) => w.type === RouteWaypointType.WAYPOINT)
      .forEach((wp, index) => form.setValue(`waypoints.${index}.altitude`, arcToFixed(wp.altitude)));
  };

  const toggleEditableRoute = () => {
    if (editableRoute) {
      const rounded = roundWaypointCoords(wps);
      dispatch(setWaypoints(rounded));
      syncAltitudeFormValues(rounded);
      dispatch(setRouteModified(true));
    }
    dispatch(setEditableRoute(!editableRoute));
  };

  return { editableRoute, toggleEditableRoute };
};

export default useRouteEditToggle;
